import { Button, InlineNotice } from "@attmos/flowstack-ui";
import { Maximize2, Minimize2, Plus, X } from "lucide-react";
import * as React from "react";
import { useMatch } from "react-router-dom";

import { useSession } from "@/api/auth";
import { useProjects } from "@/api/projects";
import { TerminalPanel } from "@/components/workspace/TerminalPanel";
import { apiErrorMessage } from "@/lib/api-error-kind";
import { cn } from "@/lib/utils";
import { dockOwner, useTerminalDockStore } from "@/stores/terminal-dock";

/**
 * The project the current route is inside (`/projects/:slug` and everything
 * under it), or null. `/projects` itself, the list, is not inside one.
 */
export function useCurrentProjectSlug(): string | null {
  const match = useMatch("/projects/:slug/*");
  return match?.params.slug ?? null;
}

/**
 * The dock's state as THIS viewer may see it.
 *
 * The persisted entry carries the session that wrote it (`owner`). Until
 * `claim` has run for the current session — one render after a different
 * viewer signs in on a shared browser — the entry is someone else's, so it
 * reads as closed with no tabs rather than flashing their project names.
 */
export function useTerminalDock(): { open: boolean; tabs: string[]; active: string | null } {
  const { data: session } = useSession();
  const owner = session ? dockOwner(session) : null;
  const claim = useTerminalDockStore((s) => s.claim);
  const mine = useTerminalDockStore((s) => owner !== null && s.owner === owner);
  const open = useTerminalDockStore((s) => s.open);
  const tabs = useTerminalDockStore((s) => s.tabs);
  const active = useTerminalDockStore((s) => s.active);

  React.useEffect(() => {
    if (owner !== null) claim(owner);
  }, [owner, claim]);

  return mine ? { open, tabs, active } : { open: false, tabs: NO_TABS, active: null };
}

const NO_TABS: string[] = [];

function tabId(slug: string): string {
  return `terminal-dock-tab-${slug}`;
}

function panelId(slug: string): string {
  return `terminal-dock-panel-${slug}`;
}

/**
 * The CloudShell-style terminal dock (fs#2533): a tab strip with one tab per
 * project, each tab that project's workspace terminal.
 *
 * Every open tab stays MOUNTED and only the active one is shown, so switching
 * tabs — or navigating to another project — never drops a terminal's
 * connection. Closing a tab unmounts its view and nothing else: the workspace
 * keeps running for its detach window, and stopping it is `TerminalPanel`'s
 * business, not the dock's. The dock starts nothing by itself either: it
 * only hands the store's one-shot start request (fs#2537 — the rail's
 * Terminal or the Workbench's "Open in dock") to the tab it names, whose
 * panel starts or attaches. A tab reached any other way — a reload, a tab
 * switch, the picker, the "+" button — shows the panel's own Start button.
 *
 * Rendered by `AppShell` as the bottom pane of its split, and only while the
 * dock is open.
 */
export function TerminalDock(): JSX.Element {
  const { tabs, active } = useTerminalDock();
  const current = useCurrentProjectSlug();
  const openTab = useTerminalDockStore((s) => s.openTab);
  const focusTab = useTerminalDockStore((s) => s.focusTab);
  const closeTab = useTerminalDockStore((s) => s.closeTab);
  const close = useTerminalDockStore((s) => s.close);
  const maximized = useTerminalDockStore((s) => s.maximized);
  const toggleMaximized = useTerminalDockStore((s) => s.toggleMaximized);
  const startRequest = useTerminalDockStore((s) => s.startRequest);
  const clearStartRequest = useTerminalDockStore((s) => s.clearStartRequest);
  const tabRefs = React.useRef(new Map<string, HTMLButtonElement>());

  // The tablist's arrow keys (the ARIA tabs pattern, automatic activation).
  const onTabKeyDown = (event: React.KeyboardEvent, slug: string): void => {
    const index = tabs.indexOf(slug);
    let next: string | undefined;
    if (event.key === "ArrowRight") next = tabs[(index + 1) % tabs.length];
    else if (event.key === "ArrowLeft") next = tabs[(index - 1 + tabs.length) % tabs.length];
    else if (event.key === "Home") next = tabs[0];
    else if (event.key === "End") next = tabs[tabs.length - 1];
    if (next === undefined) return;
    event.preventDefault();
    focusTab(next);
    tabRefs.current.get(next)?.focus();
  };

  return (
    <section
      aria-label="Terminal dock"
      className="flex h-full min-h-0 flex-col border-t border-border bg-card"
      data-testid="terminal-dock"
    >
      <div className="flex shrink-0 items-center gap-1 border-b border-border-subtle px-2 py-1">
        <div
          role="tablist"
          aria-label="Project terminals"
          className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto"
        >
          {tabs.map((slug) => {
            const selected = slug === active;
            return (
              // The close button is a SIBLING of the tab, not inside it: a
              // button nested in a `tab` is invalid ARIA, and its click would
              // also select the tab it is closing.
              <div
                key={slug}
                role="presentation"
                className={cn(
                  "flex shrink-0 items-center rounded-md border",
                  selected ? "border-border bg-muted" : "border-transparent hover:bg-muted/60"
                )}
              >
                <button
                  ref={(node) => {
                    if (node) tabRefs.current.set(slug, node);
                    else tabRefs.current.delete(slug);
                  }}
                  type="button"
                  role="tab"
                  id={tabId(slug)}
                  aria-selected={selected}
                  aria-controls={panelId(slug)}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => focusTab(slug)}
                  onKeyDown={(event) => onTabKeyDown(event, slug)}
                  className="px-2 py-1 font-mono text-xs"
                >
                  {slug}
                </button>
                <button
                  type="button"
                  aria-label={`Close the ${slug} terminal tab`}
                  title="Close the tab (the workspace keeps running)"
                  onClick={() => closeTab(slug)}
                  className="mr-1 rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>
        {current !== null && !tabs.includes(current) ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => openTab(current)}
            aria-label={`Open a terminal tab for ${current}`}
          >
            <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            <span className="font-mono text-xs">{current}</span>
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={maximized ? "Restore the terminal dock" : "Maximize the terminal dock"}
          aria-pressed={maximized}
          onClick={() => toggleMaximized()}
        >
          {maximized ? (
            <Minimize2 className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Close the terminal dock"
          onClick={() => close()}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      {/* A flex column down to the terminal's host (fs#2548): the active tab
      panel takes the rest of the dock's height, and the terminal fills it.
      The hidden panels keep `display: none` through the `hidden` class — the
      `hidden` attribute alone would lose to `flex`. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-auto p-2">
        {tabs.length === 0 ? (
          <ProjectPicker onPick={openTab} />
        ) : (
          tabs.map((slug) => (
            <div
              key={slug}
              role="tabpanel"
              id={panelId(slug)}
              aria-labelledby={tabId(slug)}
              hidden={slug !== active}
              className={cn("min-h-0 flex-1 flex-col", slug === active ? "flex" : "hidden")}
            >
              <TerminalPanel
                slug={slug}
                startRequestId={startRequest?.slug === slug ? startRequest.id : null}
                onStartRequestHandled={clearStartRequest}
              />
            </div>
          ))
        )}
      </div>
    </section>
  );
}

/** The empty dock outside a project: pick which project's terminal to open. */
function ProjectPicker({ onPick }: { onPick: (slug: string) => void }): JSX.Element {
  const projects = useProjects();
  if (projects.isLoading) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading projects…
      </p>
    );
  }
  if (projects.isError) {
    return (
      <InlineNotice tone="danger" title="Could not load your projects">
        {apiErrorMessage(projects.error)}
      </InlineNotice>
    );
  }
  const list = projects.data ?? [];
  if (list.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">You have no projects to open a terminal in.</p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">
        Open a project&apos;s terminal. Opening one does not start its workspace.
      </p>
      <ul className="flex flex-wrap gap-2" aria-label="Projects">
        {list.map((project) => (
          <li key={project.slug}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onPick(project.slug)}
              aria-label={`Open a terminal tab for ${project.slug}`}
            >
              {project.display_name || project.slug}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
